import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';
import React from 'react';

import { parseCustomTimestamp } from '../../utils/dateUtils';

const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const colorScale = {
  0: 'bg-bgAccent',
  1: 'bg-[#003F57]',
  2: 'bg-[#00648A]',
  3: 'bg-[#0089BD]',
  4: 'bg-[#00AFF0]',
  5: 'bg-[#24C3FF]',
};

const PeakHourChart = () => {
  const { data } = useCallData();
  const { dateRange } = useWeek();

  // Process data to get activity levels for each hour of each day
  const processActivityData = () => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return {};
    }

    // Calculate the number of days in the selected range
    const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
    
    // Generate dates for the selected range (use all dates in the range)
    const rangeDates = Array.from({ length: dayCount }, (_, i) => 
      addDays(dateRange.start, i)
    );
    
    const activityData = {};

    // Initialize activity data for each day
    rangeDates.forEach(date => {
      activityData[format(date, 'MM/dd')] = Array(24).fill(0);
    });

    // Count calls for each hour of each day
    data.forEach(call => {
      const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
      if (callDate) {
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);
        
        if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
          const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' });
          const hour = callDate.getUTCHours(); // Get hour directly from the parsed UTC date
          
          if (activityData[formattedDate]) {
            activityData[formattedDate][hour]++;
          }
        }
      }
    });

    // Calculate activity levels (0-5) based on call frequency
    const maxCallsPerHour = Math.max(
      ...Object.values(activityData).flatMap(hours => hours)
    );

    // Convert call counts to activity levels (0-5)
    Object.keys(activityData).forEach(date => {
      activityData[date] = activityData[date].map(callCount => {
        if (callCount === 0) return 0;
        if (maxCallsPerHour === 0) return 0;
        
        // Calculate activity level based on relative frequency
        const level = Math.ceil((callCount / maxCallsPerHour) * 5);
        return Math.min(level, 5); // Ensure we don't exceed level 5
      });
    });

    return activityData;
  };

  const activityData = processActivityData();
  const days = Object.keys(activityData);

  return (
    <div className="card-container w-full text-font-primary h-[300px] md:h-fit flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm">Peak Hours</h2>
        <div className="flex gap-2 items-center text-xs font-primary">
          <span>Less</span>
          <div className="flex flex-row items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`w-4 h-4 ${colorScale[i]} rounded-sm`} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>

      {/* Scrollable container */}
      <div className="overflow-x-auto">
        {/* Grid layout with swapped axes */}
        <div className="grid grid-cols-[40px_repeat(24,minmax(0,1fr))] items-center justify-center gap-1 h-full min-w-[800px]">
          {/* Header Row - Now shows hours */}
          <div className="w-fit"></div>
          {hours.map((hour, index) => (
            <div key={hour} className="text-center text-sm text-font-secondary">
              {index % 2 === 0 ? hour : ''}
            </div>
          ))}

          {/* Day Rows - Now shows days on the left */}
          {days.map((day) => (
            <React.Fragment key={day}>
              <div className="text-right text-sm text-font-secondary w-fit">
                {day}
              </div>
              {Array.from({ length: 24 }, (_, hourIdx) => (
                <div 
                  key={`${day}-${hourIdx}`} 
                  className={`w-full h-full ${colorScale[activityData[day][hourIdx]]} rounded-sm`} 
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PeakHourChart;
